// react
import { FC } from 'react';
// react-admin
import { useDefineAppLocation } from '@rg-admin/ra-navigation';
import { FilterWithSave } from '@rg-admin/ra-preferences';
import {
  Datagrid,
  DateField,
  EmailField,
  FunctionField,
  ListProps,
  NumberField,
  SearchInput,
  SelectInput,
  TopToolbar,
} from 'react-admin';
// mui
import { Theme, useMediaQuery } from '@material-ui/core';
// utils
import { renderAge, renderGender, dateFormat } from '../../utils/helper';
// components
import EmptyList from '../../component/EmptyList';
import PaginationWithCustomEmpty from '../../component/PaginationWithCustomEmpty';
import CustomBreadcrumb from '../../component/layout/Breadcrumb';
import EnterpriseList from '../../component/ra-enterprise/List';
import CustomerLinkField from './components/CustomerLinkField';
import DesktopGrid from './components/DesktopGrid';
import MobileGrid from './components/MobileGrid';
import Aside from './components/UsersListAside';
// -----------------------------------------------------------------------------------------------------
const UserFilter: FC = (props: any) => (
  <FilterWithSave {...props}>
    <SearchInput source="searchKey" alwaysOn />
    <SelectInput
      label="Plan"
      source="planFilter"
      choices={[
        { id: 'monthlyOld', name: 'Monthly Old' },
        { id: 'monthlyNew', name: 'Monthly New' },
        { id: 'monthlyFreeTrial', name: 'Monthly Free Trial' },
        { id: 'yearlyOld', name: 'Yearly Old' },
        { id: 'yearlyNew', name: 'Yearly New' },
        { id: 'yearlyFreeTrial', name: 'Yearly Free Trial' },
        { id: 'lifeTime', name: 'Life Time' },
      ]}
    />
  </FilterWithSave>
);

const UserListColumn = [
  <CustomerLinkField sortable={false} key="user" />,
  <EmailField sortable={false} label="Email" source="email" key="email" />,
  <FunctionField
    key="ProductId"
    label="Subscription Plan"
    render={(record: any) => {
      if (record.ProductId === '1000') return 'Lifetime';
      if (record.ProductId === '999') return 'Monthly';
      if (record.ProductId === '998') return 'Yearly';
      return record.ProductId;
    }}
  />,
  <FunctionField
    key="billingDate"
    source="billingDate"
    label="Next Billing Date"
    render={(record: any) =>
      record.ProductId === '1000' ? 'N/A' : record.billingDate
    }
  />,
  <NumberField
    key="subscriptionPrice"
    source="subscriptionPrice"
    label="Revenue"
    options={{
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'narrowSymbol',
    }}
  />,
  <FunctionField
    key="gender"
    label="Gender"
    sortable={false}
    source="gender"
    render={(record) => renderGender(record)}
  />,
  <FunctionField
    key="age"
    sortable={false}
    label="Age"
    render={(record: any) => renderAge(record)}
  />,
];
const ListActions = (props: any) => {
  const { className } = props;
  return (
    <TopToolbar className={className}>
      <CustomBreadcrumb variant="actions" />
    </TopToolbar>
  );
};
/**
 * ListSubscribedUsers component for displaying a list of subscribed users.
 * @param {Object} props - Component props
 * @returns {JSX.Element} - JSX element representing the ListSubscribedUsers component
 */
const ListSubscribedUsers: FC<ListProps> = (props) => {
  const isXsmall = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('xs')
  );
  useDefineAppLocation('users/subscribed-users');
  const isSmall = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));

  return (
    <EnterpriseList
      {...props}
      preferenceKey="subscribed-users.list"
      hasColumnsSelector={false}
      bulkActionButtons={false}
      defaultColumns={UserListColumn}
      hasViewSelector
      filters={isSmall ? <UserFilter /> : undefined}
      aside={<Aside />}
      actions={<ListActions />}
      perPage={25}
      exporter={false}
      hasCreate={false}
      title="Subscribed Users"
      pagination={<PaginationWithCustomEmpty />}
      sort={{ field: 'id', order: 'DESC' }}
    >
      {({
        view,
        hasShow,
        hasList,
        hasEdit,
        syncWithLocation,
        ...rest
      }): JSX.Element => {
        if (isXsmall) {
          return <MobileGrid />;
        }
        if (view === 'grid') {
          return <DesktopGrid fields={UserListColumn} />;
        }
        return (
          <Datagrid
            noDelete
            // rowClick="show"
            empty={<EmptyList resource={'Users'} />}
            {...rest}
          >
            {UserListColumn}
          </Datagrid>
        );
      }}
    </EnterpriseList>
  );
};

export default ListSubscribedUsers;
